'use strict';

import { action, flow, observable, makeObservable } from "mobx";

import {States} from "@uw-it-sis/lib-react/lib/AppConstants";
import events from "@uw-it-sis/lib-react/lib/Events";
import PlanEvents from "@uw-it-sis/lib-react-myplan/lib/PlanEvents";
import appStore from "@uw-it-sis/lib-react/lib/AppStore";
import {capitalize, isEmpty, isFalse, groupBy} from "@uw-it-sis/lib-react/lib/Utils";
import TermUtils from "@uw-it-sis/lib-react/lib/TermUtils";
import {normalize} from "@uw-it-sis/lib-react-myplan/lib/CourseUtils";
import AnnouncerStore from "@uw-it-sis/lib-react/lib/AnnouncerStore";

import {PlanItemType} from "../lib/Constants"

class PlanViewStore {
    state = States.initial;

    showFailureMessageModal = false;

    constructor() {
        makeObservable(this, {
            state: observable,
            showFailureMessageModal: observable,
            mount: action,
            clearFailureMessage: action
        });

        this.componentKey = "PlanView";
        this.exceptionUtils = appStore.getExceptionUtils();
        this.httpClient = appStore.getHttpClient();

        this.failureTitle = "";
        this.failureMessage = "";
        this.planData = [];
        this.year = "";
    }

    mount(year) {
        events.listen(PlanEvents.planItemTypeChanged, (eo) => {
            AnnouncerStore.announce(`${eo.description} has been moved to ${eo.newType}`);
            this.refreshData()
        });
        events.listen(PlanEvents.planItemAdded, (eo) => {
            AnnouncerStore.announce(`${eo.description} has been added in ${TermUtils.makeTermDescriptionFromTermId(eo.atpId)}`);
            this.refreshData()
        });
        events.listen(PlanEvents.planItemUpdated, (eo) => {
            AnnouncerStore.announce(`${eo.description} has been updated in ${TermUtils.makeTermDescriptionFromTermId(eo.atpId)}`);
            this.refreshData()
        });
        events.listen(PlanEvents.planItemRemoved, () => this.refreshData());
        events.listen(PlanEvents.recommendedPlanItemAccepted, () => this.refreshData());
        events.listen(PlanEvents.planItemError, (eo) => this.onPlanItemError(eo));

        this.failureMessage = "";
        this.failureTitle = "";
        this.year = year;
        this._loadData(year);
    }

    unmount() {
        events.remove(PlanEvents.planItemTypeChanged, (eo) => {
            AnnouncerStore.announce(`${eo.description} has been moved to ${eo.newType}`);
            this.refreshData()
        });
        events.remove(PlanEvents.planItemAdded, (eo) => {
            AnnouncerStore.announce(`${eo.description} has been added in ${TermUtils.makeTermDescriptionFromTermId(eo.atpId)}`);
            this.refreshData()
        });
        events.remove(PlanEvents.planItemUpdated, (eo) => {
            AnnouncerStore.announce(`${eo.description} has been updated in ${TermUtils.makeTermDescriptionFromTermId(eo.atpId)}`);
            this.refreshData()
        });
        events.remove(PlanEvents.planItemRemoved, () => this.refreshData());
        events.remove(PlanEvents.recommendedPlanItemAccepted, () => this.refreshData());
        events.remove(PlanEvents.planItemError, (eo) => this.onPlanItemError(eo));
    }

    _loadData = flow(function* (year) {
        this.state = States.pending;

        // Build url for the ajax call
        let url = `api/plan/terms?year=${year}`;

        try {
            this.planData = yield this.httpClient.doGet(url, 'getTermsForYear');

            if (appStore.isDevMode()) {
                console.log(`PlanViewStore: Data load complete for year ${year}`);
            }

            this._updatePlanData(this.planData);
        } catch(error) {
            this.exceptionUtils.handleException(error, "PlanViewStore.loadData");
            this.state = States.error;
        }
    }.bind(this));

    /**
     * Returns true if the data is available. Otherwise, false.
     */
    hasData() {
        return isFalse(isEmpty(this.planData));
    }

    /**
     * Reloads the data from the planned term service.
     */
    refreshData() {
        this._loadData(this.year);
    }

    onPlanItemError(eventObject) {
        if ( PlanEvents[eventObject.eventType] === PlanEvents.planItemTypeChanged ) {
            this.failureTitle = `Unable to Mark as ${capitalize(eventObject.newType)}`;
            this.failureMessage = eventObject.errMessage;
            this.showFailureMessageModal = true;
        } else if ( PlanEvents[eventObject.eventType] === PlanEvents.recommendedPlanItemAccepted ) {
            this.failureTitle = `Unable to accept adviser recommendation`;
            this.failureMessage = eventObject.errMessage;
            this.showFailureMessageModal = true;
        }
    }

    /**
     * Refactor the data model of academic record so that it is more similar to the
     * plan item lists, thereby enabling the use of the same components for UI display
     * Also update plan items with booleans when item is part of a block reg cluster
     * @param terms - the list of terms returned from service call
     * @private
     */
    _updatePlanData(terms) {
        terms.map((term, termIndex) => {
            // Create a list for academic record data in a model that matches plan items
            this.planData[termIndex].registeredList = [];

            // Check if there are any sections within the academic record
            if ( term.academicRecord.length > 0 ) {
                // Create an map of sections by course code since original data is just list of sections
                // course code being the key and the value being the list of sections for that course
                // course code can be used as a unique key here because we are only looking through one term

                let courseGroupings = groupBy(term.academicRecord, 'courseCode');

                // Loop the course groupings and build a plan item model
                for ( let [courseCode, sections] of Object.entries(courseGroupings) ) {
                    // Truncated plan item model template
                    let planItemObject = {
                        courseDetails: {
                            code: courseCode
                        },
                        planActivities: [],
                        statusAlerts: [],
                        sectionsAvailable: true
                    };
                    // Loop the sections and add to the planActivities
                    sections.map((section, sectionIndex) => {
                        // Get the course details from the first section
                        if ( sectionIndex === 0 ) {
                            planItemObject.courseDetails.courseId = section.courseId;
                            planItemObject.courseDetails.courseTitle = section.courseTitle;
                            planItemObject.courseDetails.credit = section.credit;
                            planItemObject.courseDetails.grade = section.grade; // Grade isn't part of the normal model
                        }
                        // Add the activityOfferingItem to the planActivities
                        planItemObject.planActivities.push(section.activityOfferingItem);
                    });
                    // Add the new registered course item to the planData
                    this.planData[termIndex].registeredList.push(planItemObject);
                }
            }

            // Update plan item lists with cluster reg status
            let groups = Object.keys(PlanItemType);
            groups.map(type => {
                this.planData[termIndex][`${type}List`] = this.setClusterStatus(this.planData[termIndex][`${type}List`]);
            });
            // Determine if any of the plan items are part of a cluster
            let hasCluster = groups.some(type => this.planData[termIndex][`${type}List`].some(item => item.plannedBlockRegistration));
            // Copy array to test if previous terms already have a cluster.
            let tempPlatData = [...this.planData];
            // Only 1 term will show the legend, so if any previous terms have the legend set to show, then dont show legend
            // If no terms have it set to show and this term has a cluster, then the legend will be shown with this term.
            this.planData[termIndex].showClusterLegend = ! tempPlatData.splice(0, termIndex).some(term => term.showClusterLegend) && hasCluster;
        });

        this.state = States.done;
    }

    /**
     * Return the plan data
     * @return array - list of years which contain plan data
     */
    getPlanData() {
        return this.planData;
    }

    clearFailureMessage() {
        this.failureMessage = "";
        this.showFailureMessageModal = false;
    }

    /**
     * Checks a list of plan items to see if planned sections make up a cluster (block reg).
     * If all sections of the cluster are planned, plan item data is updated with booleans
     * at the course level and section level to indicate it is part of a cluster.
     * @param items - a list of plan items.
     * @return - a list of plan items with cluster booleans set at course and section level.
     */
    setClusterStatus(items) {
        // If items is empty, return empty
        if ( items.length === 0 ) {
            return [];
        }

        let planItems = items;
        // temporary map of course codes with a list of SLNs planned for that course
        let itemsMap = {};

        let blocksMap = {}; // Data model for blocks with SLNs of the block
        let blocksData = {}; // Data model for blocks with course codes of the block

        // Populate the itemsMap with data from the list of plan items
        planItems.map(item => {
            // Ignore placeholders items
            if (! item.placeHolder ) {
                // Course code is map key
                let courseCode = normalize(item.courseDetails.code)
                // List of planned SLNs is the value
                itemsMap[courseCode] = item.planActivities.map(activity => {
                    if (activity.blockRegistration && activity.blockRegistration.parent) {
                        let slns = [];
                        let courseCodes = [];
                        let courses = activity.blockRegistration.courses;
                        for (let courseIndex = 0; courseIndex < courses.length; courseIndex++) {
                            courseCodes.push(activity.blockRegistration.courses[courseIndex].label);
                            let sections = activity.blockRegistration.courses[courseIndex].sections;
                            for (let sectionIndex = 0; sectionIndex < sections.length; sectionIndex++) {
                                slns.push(sections[sectionIndex].sln);
                            }
                        }
                        blocksMap[activity.registrationCode] = slns;
                        blocksData[activity.registrationCode] = courseCodes;
                    }
                    return activity.registrationCode;
                });
            }
        });

        let itemsRegCodeList = [].concat.apply([], Object.values(itemsMap));

        // Loops the plan items data to set booleans by checked data in itemsMap
        for (let itemsIndex = 0; itemsIndex < planItems.length; itemsIndex++) {
            // Skip placeholder items
            if (planItems[itemsIndex].placeHolder) {
                continue;
            }

            // Loop the activities to see if they are part of a block reg
            for (let activityIndex = 0; activityIndex < planItems[itemsIndex].planActivities.length; activityIndex++) {
                // The section data
                let activity = planItems[itemsIndex].planActivities[activityIndex];
                // The block data
                let block = activity.blockRegistration;
                // If there is block data for the section and the blocksMap is not empty
                if ( block && ! isEmpty(blocksMap) ) {
                    // Build a list of keys from blockMap and see if any of the parent SLNs of the block exist in that list, if so a parent of a block is planned
                    let isParentRegCodePlanned = Object.keys(blocksMap).some(sln => block.parentSlns.indexOf(sln) >= 0);
                    // Find the parent SLN of the planned block
                    let parentKey = Object.keys(blocksMap).find(sln => block.parentSlns.indexOf(sln) >= 0);
                    // Check the list of SLNs of the block to see if all are planned, if so the entire block is planned
                    let isEntireBlockPlanned = blocksMap.hasOwnProperty(parentKey) ? blocksMap[parentKey].every(sln => itemsRegCodeList.indexOf(sln) >= 0) : false;
                    // If the parent of the block and the entire block is planned, then set flag on section that it is part of a block reg
                    planItems[itemsIndex].planActivities[activityIndex].plannedBlockRegistration = isParentRegCodePlanned && isEntireBlockPlanned;
                    // Adds a list of all the course codes of the block to the section for UI popover display
                    if (isParentRegCodePlanned && isEntireBlockPlanned) {
                        planItems[itemsIndex].planActivities[activityIndex].plannedBlockRegistrationData = blocksData[parentKey];
                    }
                } else {
                    planItems[itemsIndex].planActivities[activityIndex].plannedBlockRegistration = false;
                }
            }
            // If any of the sections on the course are part of a planned block reg, set flag on course.
            // This is used to determine allowed actions on the course (move and changing planned/backup state not available)
            planItems[itemsIndex].plannedBlockRegistration = planItems[itemsIndex].planActivities.some(activity => activity.plannedBlockRegistration);
        }

        return planItems;
    }
}

export default new PlanViewStore();